import React, { forwardRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Icon, HelperText, FormLabel, FormControl, RfButton } from '../../index'
import withTheme from '../styles/withTheme'
import useControlled from '../../utils/useControlled'

import './TextField.scss'

/* @TODO
 * - proptypes
 * - props override
 */

const TextField = forwardRef(function TextField(props, ref) {
	const {
		autoComplete,
		autoFocus = false,
		className,
		disabled = false,
		helperText = null,
		icon,
		iconPosition = 'before',
		inputRef,
		id,
		inputProps: inputPropsProp, // @TODO - check override of attributes
		InputProps,
		isCorrect: isCorrectProp,
		label,
		labelProps,
		name,
		onBlur,
		onChange,
		onFocus,
		placeholder = ' ',
		required = false,
		rows,
		showSuccess = false,
		size = 'medium',
		tabIndex,
		type: typeProp,
		textArea = false,
		maxLength = 4000,
		maxRows,
		validator: validatorFunc,
		value,
		...other
	} = props

	const Component = textArea ? 'textarea' : 'input'

	const [type, setType] = useState(typeProp)

	const [isCorrect, setIsCorrect] = useControlled({
		controlled: isCorrectProp
	})

	const { current: isControlled } = React.useRef(isCorrectProp != null)

	useEffect(() => {
		setIsCorrect(isCorrectProp)
	}, [isCorrectProp])

	useEffect(() => {
		setType(typeProp)
	}, [typeProp])

	const handleBlur = (event) => {
		if (!isControlled) {
			let validation = null
			if (required && event.target.value.trim() == '') {
				validation = false
			} else {
				validatorFunc && (validation = validatorFunc(event.target.value))
			}
			setIsCorrect(validation)
		}
		onBlur && onBlur(event)
	}

	const togglePasswordVisibility = () => {
		if (typeProp === 'password') {
			setType(type === 'password' ? 'text' : 'password')
		}
	}

	const inputProps = {
		...inputPropsProp,
		...(inputPropsProp?.autoComplete &&
		{
			autoComplete: inputPropsProp.autoComplete === 'off' ? 'nope' : inputPropsProp.autoComplete
		}
		)
	}

	return (
		<FormControl ref={ref}>
			{textArea && label && (
				<FormLabel disabled={disabled} {...labelProps} className={clsx('Vb-textfield__label', className)}>
					{label}
				</FormLabel>
			)}
			<div
				className={clsx(
					'Vb-textfield-wrapper',
					disabled && `Vb-textfield--disabled`,
					`Vb-textfield--${size}`,
					className
				)}
			>
				{icon && !textArea && (
					<span className={clsx('Vb-textfield__icon', `Vb-textfield__icon--${iconPosition}`)}>
						<Icon size={size === 'medium' ? 22 : 18} sizeInPx>
							{icon}
						</Icon>
					</span>
				)}
				<Component
					className={clsx(
						'Vb-textfield',
						`Vb-textfield--${typeProp || 'text'}`,
						textArea && 'Vb-textfield--textarea',
						label && 'Vb-textfield--with-label',
						isCorrect !== null &&
						(isCorrect === false ? 'Vb-textfield--error' : showSuccess && 'Vb-textfield--success'),
						icon && !textArea && `Vb-textfield--with-icon-${iconPosition}`
					)}
					ref={inputRef}
					autoComplete={autoComplete === 'off' ? 'nope' : autoComplete}
					autoFocus={autoFocus}
					disabled={disabled}
					id={id}
					name={name}
					onBlur={handleBlur}
					onChange={onChange}
					onFocus={onFocus}
					maxLength={textArea ? maxLength : null}
					placeholder={placeholder}
					required={required}
					rows={textArea ? rows : null}
					tabIndex={tabIndex}
					type={textArea ? null : type}
					value={value}
					{...inputProps}
					{...InputProps}
					{...other}
				/>
				{!textArea && label && (
					<FormLabel {...labelProps} className="Vb-textfield__label">
						{label}
					</FormLabel>
				)}
				{typeProp === 'password' && (
					<RfButton
						className="Vb-textfield__password-visibility-toggler"
						variant="link"
						icon="visibility"
						tabIndex="-1"
						onClick={togglePasswordVisibility}
					/>
				)}
			</div>
			{helperText && <HelperText isError={isCorrect !== null && !isCorrect}>{helperText}</HelperText>}
		</FormControl>
	)
})

export default withTheme('Vb-textfield')(TextField)

const isValidatorFunction = (props, propName, componentName) => {
	const propFunction = props[propName]
	let value
	typeof propFunction === 'function' ? (value = propFunction()) : null

	if (typeof value !== 'undefined' && value !== null) {
		if (value !== true && value !== false) {
			return new Error(
				`${propName} in ${componentName} is not a valid function. A function that evaluates the value to true or false is required`
			)
		}
	}
	return null
}

TextField.propTypes = {
	/**
	 * This prop helps users to fill forms faster, especially on mobile devices.
	 * The name can be confusing, as it's more like an autofill.
	 * You can learn more about it [following the specification](https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#autofill).
	 */
	autoComplete: PropTypes.string,
	/**
	 * If `true`, the `input` element will be focused during the first mount.
	 */
	autoFocus: PropTypes.bool,
	/**
	 * The CSS class name of the dropdown element.
	 */
	className: PropTypes.string,
	/**
	 * If `true`, the select will be disabled.
	 */
	disabled: PropTypes.bool,
	/**
	 * The text of the HelperText element,
	 * that displays a helper text below the TextField.
	 */
	helperText: PropTypes.string,
	/**
	 * Text code from the icon list that the Icon component uses
	 * If setted, an icon will be displayed in the left side of the input
	 */
	icon: PropTypes.string,
	/**
	 * Icon position
	 * @default 'before'
	 */
	iconPosition: PropTypes.oneOf(['before', 'after']),
	/**
	 * Pass a ref to the `input` element.
	 */
	inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
	/**
	 * id of the input element
	 */
	id: PropTypes.string,
	/**
	 * [Attributes] applied to the `input` element.
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes
	 */
	inputProps: PropTypes.object,
	/**
	 * Props applied to the Input element.
	 */
	InputProps: PropTypes.object,
	/**
	 * Flag to store the validation of the input
	 * and it's resulting styling
	 */
	isCorrect: PropTypes.bool,
	/**
	 * The label associated to the input
	 */
	label: PropTypes.string,
	/**
	 * [Attributes] applied to the `label` element.
	 */
	labelProps: PropTypes.object,
	/**
	 * Name attribute of the input element
	 */
	name: PropTypes.string,
	/**
	 * Callback function fired after the TextField loses focus.
	 *
	 * @param {object} event The event source of the callback.
	 * You can pull out the new value by accessing `event.target.value` (any).
	 * @param {object} [child] The react element that was selected.
	 */
	onBlur: PropTypes.func,
	/**
	 * Callback function fired when any char is introduced into the TextField.
	 *
	 * @param {object} event The event source of the callback.
	 * You can pull out the new value by accessing `event.target.value` (any).
	 * @param {object} [child] The react element that was selected.
	 */
	onChange: PropTypes.func,
	/**
	 * @ignore
	 */
	onFocus: PropTypes.func,
	/**
	 * Placeholder of the input element
	 * By default, needs to be an empty string
	 * to help the Label animation
	 */
	placeholder: PropTypes.string,
	/**
	 * If 'true' the input will be required,
	 * and validation styles will be applied
	 */
	required: PropTypes.bool,
	/**
	 * If textArea, specifies the visible height of a text area, in lines
	 */
	rows: PropTypes.number,
	/**
	 * Textfield size
	 * @default 'medium'
	 */
	size: PropTypes.oneOf(['small', 'medium']),
	/**
	 * If 'true', in case of validated input,
	 * green outline style will be applied to it.
	 */
	showSuccess: PropTypes.bool,
	/**
	 * @ignore
	 */
	tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	/**
	 * Type of the `input` element. It should be [a valid HTML5 input type]
	 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types
	 */
	type: PropTypes.string,
	/**
	 * If 'true', a textarea form element will be rendered instead of input,
	 */
	textArea: PropTypes.bool,
	/**
	 * If textArea, specifies the maximun number of chars
	 */
	maxLength: PropTypes.number,
	/**
	 * If textArea, specifies the maximun number of lies displayed.
	 */
	maxRows: PropTypes.number,
	/**
	 * Collaborator function that is
	 */
	validator: isValidatorFunction,
	/**
	 * The input value.
	 */
	value: PropTypes.any
}
